const plural = (count: number, singular: string, pluralForm = `${singular}s`) => (count === 1 ? singular : pluralForm)

const quote = (text: string) => `“${text}”`

export const SECTION = {
    title: 'Capacity',
    backToList: 'Back to Capacity',
    reportLink: 'See usage in the Capacity report',
    newTitle: 'New capacity',
    renameNotice: 'This section was called Crowd Control. It is now Capacity, and everything you set up is still here.',
    dismiss: 'Dismiss',
}

export const BLAST_RADIUS = 'Takes effect now in the webshop, Convious POS and Convious Kiosk. Dynamic prices follow.'

export const SAVE = {
    idle: 'Save',
    create: 'Save and create',
    keepEditing: 'Keep editing',
    confirm: 'Confirm',
    saving: 'Saving…',
    nothingChanged: 'Nothing changed yet. Edit the name, the limit, how it counts or the products first.',
    nameMissing: 'Give this capacity a name first — your team needs it to tell capacities apart.',
    saved: (name: string) => `${quote(name)} saved.`,
    created: (name: string) => `${quote(name)} created.`,
    failed: 'Couldn’t save. Your changes are still here — try again.',
    partlyFailed: (names: string[]) =>
        `Saved, except changes to ${names.map(quote).join(', ')} — those are still waiting. Try again.`,
    label: (parts: string[]) =>
        parts.length === 0 ? 'Save' : parts.length <= 2 ? `Save: ${parts.join(', ')}` : `Save ${parts.length} changes`,
    retryLabel: (count: number) => `Retry ${count} ${plural(count, 'change')}`,
    parts: {
        rename: 'rename',
        create: (name: string) => `create ${quote(name)}`,
        limit: (from: string, to: string) => `limit ${from} → ${to}`,
        add: (count: number) => `add ${count} ${plural(count, 'product')}`,
        remove: (count: number) => `remove ${count} ${plural(count, 'product')}`,
        uses: (name: string, from: number, to: number) => `${name} uses ${from} → ${to}`,
        usesMany: (count: number) => `change uses for ${count} products`,
    },
    unsaved: {
        title: 'You have unsaved changes',
        text: 'Leave without saving?',
        leave: 'Leave',
        stay: 'Keep editing',
    },
}

export const CONFIG = {
    heading: 'Capacity',
    name: 'Name',
    nameHelp: 'Only your team sees this name — guests never do.',
    namePlaceholder: 'e.g. Main entrance, Splash Zone',
    limit: 'Limit',
    limitHelp: 'Leave empty for no limit. Enter 0 to close this capacity.',
    limitPlaceholder: 'No limit',
    limitInvalid: 'Must be a whole number between 0 and 100,000.',
    closed: 'Closed: nothing in this capacity can be sold.',
    belowUsed: (used: number, next: number) =>
        `${used} are already booked for today. Lowering the limit to ${next} won’t cancel them, it only stops new sales.`,
    belowUsedClosed: (used: number) =>
        `${used} are already booked for today. Closing won’t cancel them, it only stops new sales.`,
    noDefaultRule:
        'This capacity is missing its everyday limit, so nothing in it can be sold right now. Contact support to fix it.',
    howItCounts: 'How it counts',
    howItCountsQuestion: 'When do guests use this capacity?',
    counting: {
        wholeTimeSlot: {
            lead: 'Guests use the capacity for their whole time slot',
            example: 'A play area, a theatre, a car park — guests stay for the whole session.',
        },
        arrivalOnly: {
            lead: 'Guests use the capacity only when they arrive',
            example: 'A ride, a tour, a show — what matters is how many start at the same time.',
        },
    },
    countsLabel: { wholeTimeSlot: 'Whole time slot', arrivalOnly: 'Arrival only' },
    countsShort: { wholeTimeSlot: 'whole time slot', arrivalOnly: 'arrival only' },
}

export const LIMIT = {
    noLimit: 'No limit',
    closed: 'Closed',
    unknown: 'Unknown',
}

export const PRODUCTS = {
    heading: 'Products in this capacity',
    addPlaceholder: 'Add a product to this capacity…',
    addNoneLeft: 'Every product is already in this capacity.',
    bundleCannotBeAdded: 'Bundles use capacity through the products inside them. Add those products instead.',
    passCannotBeAdded: 'Passes and memberships never use capacity.',
    filterType: 'Type',
    filterOnSale: 'On sale',
    filterAll: 'All',
    filterOptions: 'Options',
    filterTickets: 'Tickets',
    filterYes: 'Yes',
    filterNo: 'No',
    showAll: 'Show all',
    count: (shown: number, total: number) => `${shown} of ${total} ${plural(total, 'product')}`,
    columns: {
        product: 'Product',
        onSale: 'On sale',
        takesUp: 'Takes up capacity',
        uses: 'Uses',
        visitorsEach: 'Visitors each',
    },
    takesUpTooltip:
        'How long a booking of this product takes up capacity. It follows how this capacity counts and the product’s time slots.',
    usesTooltip: 'How much of the limit one booking of this product uses.',
    takesUp: {
        slotEnd: 'For its whole time slot',
        slotStart: 'Only at its start time',
        dayEnd: 'For the whole day',
        dayStart: 'Only at the start of the day',
        dependsOnCategory: 'Depends on the category',
        dependsOnDate: 'Depends on the date',
        bundle: 'Bundles have no effect',
        unavailable: 'Time slots did not load',
    },
    onSale: {
        yes: 'Yes',
        no: 'No',
        none: '—',
        optionTooltip: 'Options follow the product they are added to.',
        factors: {
            enabled: 'Switched on',
            showDates: 'Inside its show dates',
            validTo: 'Not past its valid-until date',
            category: 'In an active category',
        },
        shownFrom: (date: string) => `Shown from ${date}`,
        shownUntil: (date: string) => `Shown until ${date}`,
        validUntil: (date: string) => `Valid until ${date}`,
    },
    breadcrumb: {
        none: 'No category',
        many: (count: number) => `In ${count} categories`,
        separator: ' › ',
    },
    tags: { new: 'New', changed: 'Changed', removing: 'Removing', notSaved: 'Not saved' },
    stepper: {
        more: (name: string) => `Use 1 more of the limit for ${quote(name)}`,
        less: (name: string) => `Use 1 less of the limit for ${quote(name)}`,
        atMin: 'Each booking uses at least 1.',
        atMax: 'This is the highest number a booking can use.',
        perCategory: 'This product uses the capacity per category. Change that on the product form.',
    },
    menu: {
        remove: 'Remove from this capacity',
        dontAdd: 'Don’t add',
        undoRemove: 'Undo remove',
        viewProduct: 'View product settings',
        viewOptionItem: 'View option settings',
        viewTimeSlots: 'View time slots',
        productId: (id: string | number) => `Product ID ${id}`,
        productIdLoading: 'Product ID …',
        productIdLoadingReason: 'Still loading the product ID.',
        copied: 'Copied',
        copyFailed: 'Could not copy',
        more: (name: string) => `More for ${quote(name)}`,
    },
    empty: 'No products yet — this capacity limits nothing.',
    filteredEmpty: (what: string) => `No ${what}.`,
    error: 'The products did not load. Try again in a bit.',
    retry: 'Try again',
    optionItemsCapped: 'Option items are not shown here yet — open the option group to see them.',
    optionItemsFailed: 'Option items did not load. Reload the page to try again.',
    linkedTwice: 'This product is linked to this capacity twice, so each booking counts twice.',
    rowNotSaved: 'Not saved yet — try again',
    changedElsewhere: 'Changed elsewhere since you opened this page',
    consequence: {
        wholeTimeSlot: (dayProducts: string[], slotProducts: string[]) =>
            `${list(dayProducts)} ${dayProducts.length === 1 ? 'uses' : 'use'} the capacity all day, so on a busy day ${
                dayProducts.length === 1 ? 'it' : 'they'
            } can fill every time slot of ${list(slotProducts)}.`,
        arrivalOnly: (dayProducts: string[], slotProducts: string[]) =>
            `${list(dayProducts)} ${dayProducts.length === 1 ? 'counts' : 'count'} at the start of the day, so ${
                dayProducts.length === 1 ? 'it never shares' : 'they never share'
            } the limit with the time slots of ${list(slotProducts)}.`,
    },
}

export const EXCEPTIONS = {
    heading: 'Limit exceptions',
    add: 'Add exception',
    none: 'No limit exceptions created',
}

export const LIST = {
    capacities: 'Capacities',
    capacitiesMeaning: 'Count or limit sales of groups of products.',
    search: 'Search capacities',
    clearSearch: 'Clear search',
    createNew: 'New capacity',
    columns: { name: 'Name', limit: 'Limit', counts: 'Counts', products: 'Products' },
    countsTooltip:
        'Whole time slot: a booking takes up capacity until its time slot ends, so overlapping bookings share the limit. Arrival only: it counts only at its start time, so only bookings that start together share it.',
    products: (count: number, options: number) =>
        options > 0 ? `${count} + ${options} ${plural(options, 'option')}` : `${count}`,
    empty: 'No capacities yet. A capacity limits how many bookings a group of products can take.',
    noMatch: (query: string) => `No capacities match ${quote(query)}.`,
    error: 'Your capacities did not load. Try again in a bit.',
    cannotDelete: 'Capacities can’t be deleted yet. Set the limit to Closed to stop sales.',
}

export const FORM_SECTION = {
    heading: 'Capacity',
    capacities: 'Capacities',
    timeSlots: 'Time slots',
    usesOfLimit: (amount: number) => `uses ${amount} of the limit`,
    booksPerSlot: 'this product books per time slot',
    booksWholeDay: 'this product books whole day',
    notInAny: 'Not in any capacity — sales are not limited.',
    bundle: 'Bundles don’t use this capacity themselves. The products inside them do.',
    bundleTimeSlots: 'Bundles book in the time slots of the products inside them.',
    saveProductFirst: 'Save the product first, then add it to a capacity.',
    saveCategoryFirst: 'Save the category first to see its capacities.',
    categoryProducts: (count: number) => `${count} ${plural(count, 'product')}`,
    categoryNone: 'No products in this category are in a capacity.',
    setOnProduct: 'Set on this product',
    setOnCategory: 'Set on this category',
    fromCategory: (name: string) => `From the category ${quote(name)}`,
    fromParentCategory: (name: string) => `From the parent category ${quote(name)}`,
    defaultForAll: 'Default for all categories',
    viaCategory: 'Guests get the time slots of the category they book through.',
    noTimeSlots: 'No time slots. Guests book for the whole day.',
    switchedOff: 'Time slots are switched off for its categories.',
    noGroupProduct: 'No time slot group applies to this product.',
    noGroupCategory: 'No time slot group applies to this category.',
    goToTimeSlots: 'Go to time slots',
    editTimeSlots: 'Edit time slots',
    timesLoading: 'Loading times…',
    timesFailed: 'Times did not load.',
    groupsFailed: 'Time slots did not load.',
    enabled: 'Time slots enabled',
    offWarning: (count: number) =>
        count === 1
            ? 'Turning this off makes 1 product use its capacity for the whole day.'
            : `Turning this off makes ${count} products use their capacity for the whole day.`,
    offWarningUnknown: 'Turning this off makes the products in this category use their capacity for the whole day.',
    offLine: 'Time slots are off. Products in this category are booked for the whole day.',
    everyDay: 'Every day',
    andMore: (count: number) => `and ${count} more`,
}

export const TIME_SLOTS = {
    search: 'Search a product or category',
    kind: { category: 'Category', product: 'Product' },
    views: { categories: 'Categories', products: 'Products' },
    viewBusy: 'Loading time slots',
    status: { active: 'Active', upcoming: 'Upcoming', expired: 'Expired' },
    all: 'All',
    appliesTo: 'Applies to',
    showAll: 'Show all',
    emptyForSelection: (name: string) => `No time slot groups apply to ${quote(name)}.`,
    emptyForStatus: (status: string) => `No ${status.toLowerCase()} time slot groups.`,
    emptyForBoth: (status: string, name: string) =>
        `No ${status.toLowerCase()} time slot groups apply to ${quote(name)}.`,
    none: 'There are no time slots yet.',
}

export function list(names: string[]): string {
    if (names.length === 0) {
        return ''
    }
    if (names.length === 1) {
        return names[0]
    }
    if (names.length === 2) {
        return `${names[0]} and ${names[1]}`
    }
    const shown = names.slice(0, 2)
    const rest = names.length - shown.length
    return `${shown.join(', ')} and ${rest} more`
}

export { plural, quote }
